import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { apiGet, apiPatch, setTimezoneOverride } from '../lib/api';
import { useAuth } from './AuthContext';

export interface Preferences {
  // Sidebar
  'sidebar.recentItems': 'true' | 'false';
  'sidebar.recentItemsCount': string;
  'sidebar.pinnedItems': 'true' | 'false';
  'sidebar.defaultOpen': 'true' | 'false';
  'sidebar.activeMeetings': 'true' | 'false';
  'sidebar.activeMeetingsEarlyMinutes': string;
  'sidebar.calendarWidget': 'true' | 'false';
  'sidebar.weekStartDay': string;

  // Appearance
  'appearance.theme': 'dark' | 'light';

  // Timezone
  'timezone': string; // IANA timezone or 'auto'

  // Editor
  'editor.toolbarVisible': 'true' | 'false';
  'editor.autosaveDelay': string; // ms

  // Notifications
  'toast.duration': string; // ms
  'toast.position': 'bottom-right' | 'top-right' | 'bottom-center';

  // Tasks board
  'tasks.defaultView': 'list' | 'board';
  'tasks.boardGroupBy': string;
  'tasks.boardShowEmpty': 'true' | 'false';

  // Scratchpad
  'scratchpad.enabled': 'true' | 'false';

  [key: string]: string;
}

const DEFAULTS: Preferences = {
  'sidebar.recentItems': 'true',
  'sidebar.recentItemsCount': '5',
  'sidebar.pinnedItems': 'true',
  'sidebar.defaultOpen': 'true',
  'sidebar.activeMeetings': 'true',
  'sidebar.activeMeetingsEarlyMinutes': '30',
  'sidebar.calendarWidget': 'true',
  'sidebar.weekStartDay': '0',
  'appearance.theme': 'dark',
  'timezone': 'auto',
  'editor.toolbarVisible': 'true',
  'editor.autosaveDelay': '2000',
  'toast.duration': '3000',
  'toast.position': 'bottom-right',
  'tasks.defaultView': 'list',
  'tasks.boardGroupBy': 'status',
  'tasks.boardShowEmpty': 'false',
  'scratchpad.enabled': 'true',
};

interface PreferencesContextValue {
  prefs: Preferences;
  loading: boolean;
  update: (changes: Record<string, string>) => Promise<void>;
  get: (key: keyof Preferences) => string;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<Preferences>({ ...DEFAULTS });
  const [loading, setLoading] = useState(true);

  // Load preferences when user is authenticated
  useEffect(() => {
    if (!user) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const serverPrefs = await apiGet<Record<string, string>>('/settings');
        if (!cancelled) {
          setPrefs({ ...DEFAULTS, ...serverPrefs });
        }
      } catch {
        // Use defaults on error
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Sync timezone preference to API layer
  useEffect(() => {
    const tz = prefs['timezone'];
    setTimezoneOverride(tz && tz !== 'auto' ? tz : null);
  }, [prefs]);

  // Apply theme to document root
  useEffect(() => {
    const theme = prefs['appearance.theme'] || 'dark';
    document.documentElement.setAttribute('data-theme', theme);
  }, [prefs]);

  const update = useCallback(async (changes: Record<string, string>) => {
    // Optimistic update
    setPrefs(prev => ({ ...prev, ...changes }) as Preferences);
    try {
      const updated = await apiPatch<Record<string, string>>('/settings', changes);
      setPrefs({ ...DEFAULTS, ...updated });
    } catch {
      // Revert on error — reload from server
      const serverPrefs = await apiGet<Record<string, string>>('/settings');
      setPrefs({ ...DEFAULTS, ...serverPrefs });
      throw new Error('Failed to save preferences');
    }
  }, []);

  const get = useCallback((key: keyof Preferences) => {
    return prefs[key] ?? DEFAULTS[key] ?? '';
  }, [prefs]);

  return (
    <PreferencesContext.Provider value={{ prefs, loading, update, get }}>
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences(): PreferencesContextValue {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used within PreferencesProvider');
  return ctx;
}

/** Convenience hook for autosave delay in ms. */
export function useAutosaveDelay(): number {
  const { prefs } = usePreferences();
  return parseInt(prefs['editor.autosaveDelay'] || '2000', 10) || 2000;
}
